/**
 * A crew, as a card you can look at without going there.
 *
 * What opens when somebody clicks a tag. It used to be the whole crew screen
 * squeezed into a dialog, which is a strange thing to do to somebody who
 * pressed four characters in a chat line: they wanted to know who these
 * people are, not to be moved into a second application.
 *
 * So this answers exactly the questions a tag raises - what is this crew,
 * how big, where, what do they play, who is in it, am I in it - and then
 * gets out of the way with one button to the real thing.
 */

import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { go } from '@/lib/hash';
import { useBackdropClose } from '@/lib/backdrop';
import { useLayer } from '@/lib/layers';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Avatar } from './Avatar';
import { GameMark } from './GameMark';
import { CrewTag } from './CrewTag';
import { CrewBanner, CrewMark } from './CrewArt';
import { Button, useToast } from './ui';
import { openUserCard } from './user';
import type { Crew } from './Crew';

export function CrewPreview({ tag, onClose, onOpenFull }: {
  tag: string;
  onClose: () => void;
  /** Take me to the crew properly. */
  onOpenFull: () => void;
}) {
  const backdrop = useBackdropClose(onClose);
  const q = useQuery({
    queryKey: ['crew', tag],
    queryFn: () => api.get<{ crew: Crew }>('/api/crews/' + encodeURIComponent(tag))
  });
  const crew = q.data?.crew ?? null;

  /* On the stack, so a press on the dimmed area takes the profile card that
     was opened from in here with it rather than leaving it behind. */
  const shell = useRef<HTMLDivElement>(null);
  useLayer(true, { node: () => shell.current, close: onClose });

  return (
    <div
      ref={shell}
      data-keeps-card
      className="soft-in fixed inset-0 z-[300] grid place-items-center bg-[rgba(3,5,8,.66)] p-4 backdrop-blur-[4px]"
      {...backdrop}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={crew ? crew.name : 'Crew'}
        className="profile-in flex w-full max-w-[1000px] flex-col overflow-hidden pugo-panel rounded-[calc(22px*var(--radius-mul,1))] border border-line bg-surface-0 shadow-[var(--shadow-3)]"
      >
        {!crew ? (
          <p className="p-12 text-center text-[13.5px] text-mute">
            {q.isError ? 'No crew with that tag.' : 'Opening…'}
          </p>
        ) : (
          <Body crew={crew} onClose={onClose} onOpenFull={onOpenFull} />
        )}
      </div>
    </div>
  );
}

function Body({ crew, onClose, onOpenFull }: { crew: Crew; onClose: () => void; onOpenFull: () => void }) {
  const catalog = useSession((s) => s.catalog);
  const toast = useToast();
  const mine = crew.myRole;
  const region = catalog?.regions.find((r) => r.id === crew.region)?.name ?? crew.region;

  /* What the crew actually plays, counted from its members - a list somebody
     typed in once is a list that is wrong by the second month. */
  const plays = (() => {
    const count = new Map<string, number>();
    for (const m of crew.members) for (const g of m.user.games ?? []) count.set(g.id, (count.get(g.id) ?? 0) + 1);
    return [...count.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5).map(([id]) => id);
  })();

  const level = crew.level;

  async function join() {
    try {
      await api.post('/api/crews/' + crew.id + '/join', {});
      toast(crew.settings.join === 'open' ? 'Welcome to ' + crew.name : 'Request sent', 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  return (
    <>
      {/* ---- the banner, and who they are ---- */}
      <div className="relative">
        <CrewBanner appearance={crew.appearance} className="h-[150px] w-full" />
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-3 right-3 grid h-9 w-9 place-items-center rounded-full bg-[rgba(0,0,0,.5)] text-white/85 backdrop-blur-sm transition-colors hover:bg-[rgba(0,0,0,.72)] hover:text-white"
        >
          <Icon name="close" size={16} />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-4 px-5 pt-4 pb-4 sm:px-6">
        <span className="pugo-panel -mt-14 flex-none rounded-[calc(18px*var(--radius-mul,1))] bg-surface-0 p-1 shadow-[0_12px_30px_-12px_rgba(0,0,0,.6)]">
          <CrewMark tag={crew.tag} appearance={crew.appearance} size={84} className="rounded-[calc(16px*var(--radius-mul,1))]" />
        </span>

        <div className="min-w-[200px] flex-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <h2 className="m-0 truncate text-[26px] leading-tight font-extrabold tracking-[-.02em] text-ink uppercase">{crew.name}</h2>
            <CrewTag crew={crew.chip} size="md" still />
          </div>
          <p className="mt-1 mb-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13.5px] text-mute">
            <span>{crew.memberCount} {crew.memberCount === 1 ? 'member' : 'members'}</span>
            <span aria-hidden>·</span>
            <span>{region}</span>
            <span aria-hidden>·</span>
            <span>{crew.language}</span>
          </p>
        </div>

        {mine === 'owner' ? (
          <span className="flex flex-none items-center gap-2 rounded-full bg-[color-mix(in_srgb,var(--good)_16%,transparent)] px-3.5 py-2 text-[13.5px] font-bold text-good">
            <Icon name="checkCirc" size={16} />
            You own this crew
          </span>
        ) : mine ? (
          <span className="flex flex-none items-center gap-2 rounded-full bg-accent-soft px-3.5 py-2 text-[13.5px] font-bold text-accent">
            <Icon name="checkCirc" size={16} />
            You are {mine === 'officer' ? 'an officer' : 'a member'}
          </span>
        ) : (
          <Button kind="primary" disabled={crew.settings.join === 'invite'} onClick={() => void join()}>
            {crew.settings.join === 'open' ? 'Join crew' : crew.settings.join === 'request' ? 'Ask to join' : 'Invite only'}
          </Button>
        )}
      </div>

      {/* ---- about, and who is in it ---- */}
      <div className="grid gap-5 border-t border-line-soft px-5 py-5 sm:px-6 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
        <div className="min-w-0">
          <h3 className="m-0 mb-1.5 text-[16px] font-bold text-ink">About</h3>
          <p className={cn('m-0 text-[14px] leading-relaxed', crew.description ? 'text-dim' : 'text-mute')}>
            {crew.description || 'No description yet.'}
          </p>

          <div className="mt-4 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-3.5">
            <div className="flex items-baseline gap-3">
              <span className="text-[14px] font-bold text-accent">Crew level {level.level}</span>
              <span className="flex-1" />
              <span className="text-[12.5px] text-mute tabular-nums">{level.into} / {level.need}</span>
            </div>
            <span className="mt-2 block h-1.5 overflow-hidden rounded-full bg-surface-4">
              <span
                className="block h-full rounded-full bg-[linear-gradient(90deg,var(--accent),var(--accent-hi))]"
                style={{ width: (level.need ? Math.round((level.into / level.need) * 100) : 0) + '%' }}
              />
            </span>
          </div>

          {plays.length ? (
            <>
              <h3 className="m-0 mt-4 mb-2 text-[15px] font-bold text-ink">Games we play</h3>
              <div className="flex flex-wrap gap-2">
                {plays.map((id) => {
                  const g = catalog?.games.find((x) => x.id === id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => { onClose(); go('parties?game=' + encodeURIComponent(id)); }}
                      className="flex items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 px-3 py-2 text-[13px] font-semibold text-dim outline-none transition-colors hover:bg-surface-3 hover:text-ink"
                    >
                      <GameMark id={id} size={18} plain />
                      {g?.name ?? id}
                    </button>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>

        <div className="min-w-0 md:border-l md:border-line-soft md:pl-5">
          <div className="mb-2.5 flex items-center gap-2">
            <h3 className="m-0 flex-1 text-[16px] font-bold text-ink">Members</h3>
            <span className="rounded-full bg-surface-3 px-2 py-px text-[12.5px] font-semibold text-dim tabular-nums">{crew.memberCount}</span>
          </div>
          {crew.membersHidden ? (
            <p className="m-0 text-[13.5px] text-mute">This crew keeps its member list to itself.</p>
          ) : (
            <ul className="m-0 flex max-h-[220px] list-none flex-col gap-1 overflow-y-auto p-0">
              {crew.members.slice(0, 20).map((m) => (
                <li key={m.user.id}>
                  <button
                    type="button"
                    onClick={(e) => openUserCard(m.user, e.currentTarget)}
                    className="flex w-full items-center gap-2.5 rounded-[calc(12px*var(--radius-mul,1))] px-2 py-2 text-left outline-none transition-colors hover:bg-surface-2"
                  >
                    <Avatar user={m.user} size={32} presence />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] font-bold text-ink">{m.user.displayName}</span>
                      <span className="block truncate text-[12px] text-mute">
                        {m.role === 'owner' ? 'Owner' : m.role === 'officer' ? 'Officer' : 'Member'}
                      </span>
                    </span>
                    {m.role === 'owner' ? <Icon name="crown" size={15} className="flex-none text-warn" /> : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* ---- the way out ---- */}
      <div className="flex flex-wrap items-center gap-3 border-t border-line-soft px-5 py-4 sm:px-6">
        {mine === 'owner' || mine === 'officer' ? (
          <Button kind="outline" icon={<Icon name="sliders" size={16} />} onClick={onOpenFull}>Crew settings</Button>
        ) : null}
        <span className="flex-1" />
        <Button kind="primary" icon={<Icon name="users" size={16} />} onClick={onOpenFull}>Open crew</Button>
      </div>
    </>
  );
}
